import React, { useState, useEffect } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import {
  Typography,
  Box,
  Paper,
  Grid,
  Button,
  TextField,
  Card,
  CardContent,
  CardActions,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Chip,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BusinessIcon from '@mui/icons-material/Business';
import { getBusinessAssessment, saveBusinessAssessment, fetchBusinessAssessmentFromSOW } from '../../utils/discoveryPhaseService';

// Styled components
const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  marginBottom: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  '& svg': {
    marginRight: theme.spacing(1),
  },
}));

const BusinessAssessmentPage = () => {
  const { projectId } = useParams();
  const { project } = useOutletContext();
  
  const [assessment, setAssessment] = useState({
    overview: '',
    currentBusinessProcesses: [],
    businessChallenges: [],
    businessObjectives: [],
    successMetrics: []
  });
  
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Edit states
  const [editMode, setEditMode] = useState(false);
  const [editingAssessment, setEditingAssessment] = useState({
    overview: '',
    currentBusinessProcesses: [],
    businessChallenges: [],
    businessObjectives: [],
    successMetrics: []
  });
  
  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [dialogItem, setDialogItem] = useState({});
  const [dialogItemIndex, setDialogItemIndex] = useState(null);
  
  // Load business assessment data from database when component mounts
  useEffect(() => {
    if (projectId) {
      loadBusinessAssessmentFromDB();
    }
  }, [projectId]);
  
  // Load business assessment from database
  const loadBusinessAssessmentFromDB = async () => {
    setLoading(true);
    try {
      const assessmentData = await getBusinessAssessment(projectId);
      if (assessmentData) {
        setAssessment(assessmentData);
        setEditingAssessment(assessmentData);
      }
    } catch (error) {
      console.error('Error loading business assessment:', error);
      showSnackbar('Error loading business assessment data', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  // Save business assessment to database
  const handleSave = async () => {
    setLoading(true);
    try {
      await saveBusinessAssessment(projectId, editingAssessment);
      setAssessment(editingAssessment);
      setEditMode(false);
      showSnackbar('Business assessment saved successfully!', 'success');
    } catch (error) {
      console.error('Error saving business assessment:', error);
      showSnackbar('Error saving business assessment', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch business assessment from project documents using AI
  const handleFetchFromSOW = async () => {
    setAiLoading(true);
    try {
      const response = await fetchBusinessAssessmentFromSOW(projectId);
      if (response.data) {
        setEditingAssessment(response.data);
        setAssessment(response.data);
        showSnackbar('Business assessment generated from project documents successfully!', 'success');
      }
    } catch (error) {
      console.error('Error fetching business assessment from project documents:', error);
      showSnackbar('Error generating business assessment from project documents', 'error');
    } finally {
      setAiLoading(false);
    }
  };
  
  // Dialog functions
  const handleOpenDialog = (type, item = null, index = null) => {
    setDialogType(type);
    
    if (type === 'currentBusinessProcesses') {
      setDialogItem(item || { name: '', description: '', currentState: '', painPoints: [] });
    } else if (type === 'businessChallenges') {
      setDialogItem(item || { challenge: '', impact: '', priority: 'Medium' });
    } else if (type === 'businessObjectives') {
      setDialogItem(item || { objective: '', measurableOutcome: '', timeline: '' });
    } else if (type === 'successMetrics') {
      setDialogItem(item || { metric: '', currentValue: '', targetValue: '', measurementMethod: '' });
    }
    
    setDialogItemIndex(index);
    setOpenDialog(true);
  };
  
  const handleSaveDialog = () => {
    const newData = { ...editingAssessment };
    
    if (dialogItemIndex !== null) {
      // Edit existing item
      newData[dialogType][dialogItemIndex] = dialogItem;
    } else {
      // Add new item
      newData[dialogType].push(dialogItem);
    }
    
    setEditingAssessment(newData);
    setOpenDialog(false);
  };
  
  const handleDeleteItem = (type, index) => {
    const newData = { ...editingAssessment };
    newData[type].splice(index, 1);
    setEditingAssessment(newData);
  };
  
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };
  
  const renderProcessCard = (process, index) => (
    <Card key={index} sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>{process.name}</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          {process.description}
        </Typography>
        <Typography variant="body2" paragraph>
          <strong>Current State:</strong> {process.currentState}
        </Typography>
        {process.painPoints && process.painPoints.length > 0 && (
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Pain Points:</strong>
            </Typography>
            <List dense>
              {process.painPoints.map((point, idx) => (
                <ListItem key={idx} sx={{ pl: 2 }}>
                  <ListItemText primary={`• ${point}`} />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </CardContent>
      {editMode && (
        <CardActions>
          <IconButton 
            size="small" 
            onClick={() => handleOpenDialog('currentBusinessProcesses', process, index)}
          >
            <EditIcon />
          </IconButton>
          <IconButton 
            size="small" 
            onClick={() => handleDeleteItem('currentBusinessProcesses', index)}
          >
            <DeleteIcon />
          </IconButton>
        </CardActions>
      )}
    </Card>
  );
  
  const renderChallengeChip = (challenge, index) => (
    <Chip 
      key={index}
      label={`${challenge.challenge} (${challenge.priority})`}
      variant="outlined"
      sx={{ m: 0.5 }}
      onDelete={editMode ? () => handleDeleteItem('businessChallenges', index) : undefined}
      onClick={editMode ? () => handleOpenDialog('businessChallenges', challenge, index) : undefined}
    />
  );
  
  if (loading && !assessment.overview) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <SectionTitle variant="h4">
            <BusinessIcon />
            Business Assessment
          </SectionTitle>
          <Box>
            {!editMode ? (
              <>
                <Button
                  variant="contained"
                  startIcon={<AutoAwesomeIcon />}
                  onClick={handleFetchFromSOW}
                  disabled={aiLoading}
                  sx={{ mr: 1, backgroundColor: '#F5821F' }}
                >
                  {aiLoading ? <CircularProgress size={20} /> : 'Fetch From Project Documents'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => setEditMode(true)}
                >
                  Edit
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  disabled={loading}
                  sx={{ mr: 1 }}
                >
                  {loading ? <CircularProgress size={20} /> : 'Save'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={() => {
                    setEditMode(false);
                    setEditingAssessment(assessment);
                  }}
                >
                  Cancel
                </Button>
              </>
            )}
          </Box>
        </Box>
        
        <Typography variant="body1" color="text.secondary">
          Analyze the current business state, challenges, objectives, and success metrics for {project?.name}.
        </Typography>
      </Paper>
      
      {/* Overview Section */}
      <Accordion expanded={true}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Overview</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {editMode ? (
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Business Assessment Overview"
              value={editingAssessment.overview}
              onChange={(e) => setEditingAssessment({
                ...editingAssessment,
                overview: e.target.value
              })}
            />
          ) : (
            <Typography>
              {assessment.overview || 'No overview available. Click "Edit" to add content or "Fetch From Project Documents" to generate from your project documents.'}
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>
      
      {/* Current Business Processes */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Current Business Processes</Typography>
          <Chip 
            label={editingAssessment.currentBusinessProcesses.length} 
            size="small" 
            sx={{ ml: 2 }} 
          />
        </AccordionSummary>
        <AccordionDetails>
          <Box>
            {editMode && (
              <Button
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog('currentBusinessProcesses')}
                sx={{ mb: 2 }}
              >
                Add Process
              </Button>
            )}
            {editingAssessment.currentBusinessProcesses.map((process, index) => 
              renderProcessCard(process, index)
            )}
            {editingAssessment.currentBusinessProcesses.length === 0 && (
              <Typography color="text.secondary" style={{ fontStyle: 'italic' }}>
                No business processes defined yet.
              </Typography>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
      
      {/* Business Challenges */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Business Challenges</Typography>
          <Chip 
            label={editingAssessment.businessChallenges.length} 
            size="small" 
            sx={{ ml: 2 }} 
          />
        </AccordionSummary>
        <AccordionDetails>
          <Box>
            {editMode && (
              <Button
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog('businessChallenges')}
                sx={{ mb: 2 }}
              >
                Add Challenge
              </Button>
            )}
            <Box>
              {editingAssessment.businessChallenges.map((challenge, index) => 
                renderChallengeChip(challenge, index)
              )}
            </Box>
            {editingAssessment.businessChallenges.length === 0 && (
              <Typography color="text.secondary" style={{ fontStyle: 'italic' }}>
                No business challenges defined yet.
              </Typography>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
      
      {/* Business Objectives */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Business Objectives</Typography>
          <Chip 
            label={editingAssessment.businessObjectives.length} 
            size="small" 
            sx={{ ml: 2 }} 
          />
        </AccordionSummary>
        <AccordionDetails>
          <Box>
            {editMode && (
              <Button
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog('businessObjectives')}
                sx={{ mb: 2 }}
              >
                Add Objective
              </Button>
            )}
            <Grid container spacing={2}>
              {editingAssessment.businessObjectives.map((objective, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>{objective.objective}</Typography>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        <strong>Success Criteria:</strong> {objective.measurableOutcome}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Timeline:</strong> {objective.timeline}
                      </Typography>
                    </CardContent>
                    {editMode && (
                      <CardActions>
                        <IconButton 
                          size="small" 
                          onClick={() => handleOpenDialog('businessObjectives', objective, index)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          onClick={() => handleDeleteItem('businessObjectives', index)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </CardActions>
                    )}
                  </Card>
                </Grid>
              ))}
            </Grid>
            {editingAssessment.businessObjectives.length === 0 && (
              <Typography color="text.secondary" style={{ fontStyle: 'italic' }}>
                No business objectives defined yet.
              </Typography>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
      
      {/* Success Metrics */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Success Metrics</Typography>
          <Chip 
            label={editingAssessment.successMetrics.length} 
            size="small" 
            sx={{ ml: 2 }} 
          />
        </AccordionSummary>
        <AccordionDetails>
          <Box>
            {editMode && (
              <Button
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog('successMetrics')}
                sx={{ mb: 2 }}
              >
                Add Metric
              </Button>
            )}
            <Grid container spacing={2}>
              {editingAssessment.successMetrics.map((metric, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>{metric.metric}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Current:</strong> {metric.currentValue}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Target:</strong> {metric.targetValue}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Measurement:</strong> {metric.measurementMethod}
                      </Typography>
                    </CardContent>
                    {editMode && (
                      <CardActions>
                        <IconButton 
                          size="small" 
                          onClick={() => handleOpenDialog('successMetrics', metric, index)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          onClick={() => handleDeleteItem('successMetrics', index)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </CardActions>
                    )}
                  </Card>
                </Grid>
              ))}
            </Grid>
            {editingAssessment.successMetrics.length === 0 && (
              <Typography color="text.secondary" style={{ fontStyle: 'italic' }}>
                No success metrics defined yet.
              </Typography>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
      
      {/* Dialog for adding/editing items */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {dialogItemIndex !== null ? 'Edit' : 'Add'} {dialogType.replace(/([A-Z])/g, ' $1').toLowerCase()}
        </DialogTitle>
        <DialogContent>
          {/* Dialog content would be rendered based on dialogType */}
          {dialogType === 'currentBusinessProcesses' && (
            <Box>
              <TextField
                fullWidth
                label="Process Name"
                value={dialogItem.name || ''}
                onChange={(e) => setDialogItem({ ...dialogItem, name: e.target.value })}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Description"
                value={dialogItem.description || ''}
                onChange={(e) => setDialogItem({ ...dialogItem, description: e.target.value })}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Current State"
                value={dialogItem.currentState || ''}
                onChange={(e) => setDialogItem({ ...dialogItem, currentState: e.target.value })}
                sx={{ mb: 2 }}
              />
            </Box>
          )}
          
          {dialogType === 'businessChallenges' && (
            <Box>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Challenge Description"
                value={dialogItem.challenge || ''}
                onChange={(e) => setDialogItem({ ...dialogItem, challenge: e.target.value })}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Business Impact"
                value={dialogItem.impact || ''}
                onChange={(e) => setDialogItem({ ...dialogItem, impact: e.target.value })}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                select
                label="Priority"
                value={dialogItem.priority || 'Medium'}
                onChange={(e) => setDialogItem({ ...dialogItem, priority: e.target.value })}
                SelectProps={{ native: true }}
              >
                <option value="High">High</option>
                <option value="Medium">Medium</option>
                <option value="Low">Low</option>
              </TextField>
            </Box>
          )}
          
          {dialogType === 'businessObjectives' && (
            <Box>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Objective Description"
                value={dialogItem.objective || ''}
                onChange={(e) => setDialogItem({ ...dialogItem, objective: e.target.value })}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Measurable Outcome"
                value={dialogItem.measurableOutcome || ''}
                onChange={(e) => setDialogItem({ ...dialogItem, measurableOutcome: e.target.value })}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Timeline"
                value={dialogItem.timeline || ''}
                onChange={(e) => setDialogItem({ ...dialogItem, timeline: e.target.value })}
              />
            </Box>
          )}
          
          {dialogType === 'successMetrics' && (
            <Box>
              <TextField
                fullWidth
                label="Metric Name"
                value={dialogItem.metric || ''}
                onChange={(e) => setDialogItem({ ...dialogItem, metric: e.target.value })}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Current Value"
                value={dialogItem.currentValue || ''}
                onChange={(e) => setDialogItem({ ...dialogItem, currentValue: e.target.value })}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Target Value"
                value={dialogItem.targetValue || ''}
                onChange={(e) => setDialogItem({ ...dialogItem, targetValue: e.target.value })}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Measurement Method"
                value={dialogItem.measurementMethod || ''}
                onChange={(e) => setDialogItem({ ...dialogItem, measurementMethod: e.target.value })}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveDialog} variant="contained">
            {dialogItemIndex !== null ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BusinessAssessmentPage;